import {
  createAsyncThunk,
  type Action,
  type ThunkAction,
} from '@reduxjs/toolkit';
import { t } from 'i18next';

import {
  emergencyAddressInformationSelector,
  isEmergencyDialbackSelector,
} from './emergencySelectors';
import {
  markAddressVerificationNeeded,
  markAsEmergencyCall,
  markAsLoggedOutEmergencyCall,
  setEmergencyAddressInformation,
  setEmergencyConnectionPoint,
  setEmergencyCoordinates,
  setIsEmergencyDeafReconnectionNeeded,
  setRegisteredAddress,
} from './emergencySlice';

import { isTeamingStartedSelector } from 'features/teaming/teaming-base/store';
import { sendRnsNotification } from 'features/rns/store';
import { CallNotificationType } from 'features/call/call-events/enums';
import type { RootState } from 'features/app/store/store';
import { AudioPlayer } from 'features/audio-player/services';
import type { TerminationType } from 'features/call/call-base/enums';
import {
  setCallTakenTime,
  setRelayCallState,
  setUpdatedCall,
} from 'features/call/call-base/store';
import {
  callIdSelector,
  selectActualCallOwnerUserId,
} from 'features/call/call-base/store/selectors';
import {
  deafPhoneNumberSelector,
  setDeafPhoneNumber,
} from 'features/call/call-deaf/store';
import { VrsCallType } from 'features/call/vrs-call/enums';
import { setVrsCallType } from 'features/call/vrs-call/store';
import { vrsCallTypeSelector } from 'features/call/vrs-call/store/vrsCallSelectors';
import { callerSessionAnalyticsInfo } from 'features/caller-session/helpers';
import { incomingCallAccepted } from 'features/caller-session/store';
import { EmergencyConnectionPoint } from 'features/emergency/enums';
import {
  regularEmergencyCallAnalytics,
  spawnEmergencyCallAnalytics,
} from 'features/emergency/helpers';
import type { EmergencyAddressAndProvisionStatus } from 'features/emergency/interfaces';
import { EmergencyDaoService } from 'features/emergency/services';
import { handleError } from 'features/notification/store';
import { rnsConnectionIdSelector } from 'features/session/store';
import { userIdSelector } from 'features/user/store';
import { toIsoWithTimezone } from 'features/utils/helpers';

export const takeEmergencyCall = createAsyncThunk(
  'emergency/takeCall',
  async (payload, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      const deafPhoneNumber = deafPhoneNumberSelector(state);
      dispatch(incomingCallAccepted(callId.toString(), deafPhoneNumber));
      dispatch(callerSessionAnalyticsInfo('Accepted emergency incoming call'));
      const isEmergencyDialback = isEmergencyDialbackSelector(state);
      const { isGeoCoordinateAvailable, verify911Address } =
        emergencyAddressInformationSelector(state);

      const { call, provision } = await EmergencyDaoService.takeEmergencyCall({
        callId,
        userId: userIdSelector(state),
        sessionId: rnsConnectionIdSelector(state),
      });
      dispatch(setEmergencyAddressInformation(call));
      dispatch(setParticipantAddress(provision));

      dispatch(setConnectionPoint(provision.isProvisioned));
      dispatch(
        regularEmergencyCallAnalytics({
          methodName: 'takeEmergencyCall',
          isProvisioned: provision.isProvisioned,
          isGeoCoordinateAvailable,
          verify911Address,
          isEmergencyDialback,
        })
      );

      dispatch(setRelayCallState(call.CallState));
      dispatch(
        setCallTakenTime(toIsoWithTimezone(call.TakenOrAbandonDateTime))
      );
      return call;
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'takeEmergencyCall',
        })
      );
      return rejectWithValue(error);
    } finally {
      AudioPlayer.stop();
    }
  }
);

export const spawnEmergencyCall = createAsyncThunk(
  'emergency/spawnCall',
  async (
    terminationReason: TerminationType,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const state = getState() as RootState;
      const callId = callIdSelector(state);
      const sessionId = rnsConnectionIdSelector(state);
      const callType = vrsCallTypeSelector(state);
      const isTeamingStarted = isTeamingStartedSelector(state);
      const userId = selectActualCallOwnerUserId(state);

      const { call, provision } = await EmergencyDaoService.spawnEmergencyCall({
        callId,
        sessionId,
        terminationReason,
        userId,
      });
      const { isGeoCoordinateAvailable, verify911Address } =
        emergencyAddressInformationSelector(state);

      dispatch(setParticipantAddress(provision));

      if (isTeamingStarted) {
        await dispatch(
          sendRnsNotification({
            eventName: CallNotificationType.SYNC_EMERGENCY_CALL_PROVISION,
            data: provision,
          })
        ).unwrap();
      }

      if (callType !== VrsCallType.EMERGENCY_CALL) {
        dispatch(setConnectionPoint(provision.isProvisioned));
      }

      dispatch(
        spawnEmergencyCallAnalytics({
          methodName: 'spawnEmergencyCall',
          isProvisioned: provision.isProvisioned,
          isGeoCoordinateAvailable,
          verify911Address,
          newCallId: call.Id,
        })
      );
      dispatch(setUpdatedCall(call));
      dispatch(markAsEmergencyCall());
      dispatch(setVrsCallType(VrsCallType.EMERGENCY_CALL));

      return call.Id;
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'spawnEmergencyCall',
        })
      );
      return rejectWithValue(error);
    }
  }
);

export const setParticipantAddress = createAsyncThunk(
  'emergency/participantAddress',
  async (provision: EmergencyAddressAndProvisionStatus, { dispatch }) => {
    if (Object.keys(provision).length) {
      dispatch(
        setRegisteredAddress({
          name: `${prepareRegisteredAddress(provision)}${provision.zip}`,
          status: provision.isProvisioned ? 'approved' : 'pending',
        })
      );
    } else {
      dispatch(
        handleError({
          error: 'Error',
          title: t('notRegisteredAddress'),
          methodName: 'setParticipantAddress',
        })
      );
    }
  }
);

export const setConnectionPoint = createAsyncThunk(
  'emergency/connectionPoint',
  async (isProvisioned: boolean, { dispatch, getState }) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const { isGeoCoordinateAvailable, verify911Address } =
      emergencyAddressInformationSelector(state);

    if (isProvisioned && !isGeoCoordinateAvailable && !verify911Address) {
      dispatch(setEmergencyConnectionPoint(EmergencyConnectionPoint.PSAP));
      return;
    }

    if (isProvisioned && !isGeoCoordinateAvailable && verify911Address) {
      dispatch(markAddressVerificationNeeded());
      dispatch(setIsEmergencyDeafReconnectionNeeded(true));
      return;
    }

    if (!isProvisioned && !isGeoCoordinateAvailable) {
      dispatch(setEmergencyConnectionPoint(EmergencyConnectionPoint.ECRC));
      return;
    }

    if (isGeoCoordinateAvailable) {
      const emergencyCoordinates =
        await EmergencyDaoService.getEmergencyCoordinates(callId);

      dispatch(setEmergencyConnectionPoint(EmergencyConnectionPoint.PSAP));
      dispatch(
        setEmergencyCoordinates({
          latitude: emergencyCoordinates.Latitude,
          altitude: emergencyCoordinates.Altitude,
          longitude: emergencyCoordinates.Longitude,
          uncertainty: emergencyCoordinates.Uncertainty,
        })
      );
    }
  }
);

export const setCallbackAddress = createAsyncThunk(
  'emergency/callbackAddress',
  async (endpointMacAddress: string, { dispatch }) => {
    try {
      const provision =
        await EmergencyDaoService.getProvision(endpointMacAddress);

      dispatch(setParticipantAddress(provision));
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'setCallbackAddress',
        })
      );
    }
  }
);

const prepareRegisteredAddress = (
  provision: EmergencyAddressAndProvisionStatus
) => {
  const startAddress =
    provision.apartment || provision.street || provision.city
      ? `${provision.apartment} ${provision.street}\n${provision.city}, `
      : '';
  const endAddress = provision.state ? `${provision.state}, ` : '';

  return `${startAddress}${endAddress}`;
};

export const detectedLoggedOutEmergencyCall =
  (
    placeholderPhoneNumber: string
  ): ThunkAction<void, RootState, unknown, Action> =>
  async (dispatch: any) => {
    dispatch(setDeafPhoneNumber(placeholderPhoneNumber));
    dispatch(markAsLoggedOutEmergencyCall());
  };
